import React, { useEffect, useMemo, useRef } from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { useTranslation } from "react-i18next";

export const SKILL_OPTION_KEYS = [
  "receptionist",
  "faq",
  "ticket_seller",
  "ticket_preseller",
  "passagem_recovery",
  "proposal_generator",
  "contract_generator",
  "appointment_scheduler",
  "sdr",
  "social_media",
  "product_seller",
  "complaints_suggestions",
  "ops_intelligence",
];

/** @deprecated use SKILL_OPTION_KEYS */
export const SKILL_OPTIONS = [
  { skill_key: "receptionist", label: "Recepcionista", desc: "Triagem e encaminhamento inteligente" },
  { skill_key: "faq", label: "Perguntas frequentes", desc: "Responde dúvidas comuns" },
  { skill_key: "ticket_seller", label: "Venda de passagens (BPe)", desc: "Fluxo de passagem e BPe" },
  { skill_key: "proposal_generator", label: "Proposta comercial (PDF)", desc: "Gera propostas novas com IA a partir de um modelo de referência" },
  { skill_key: "contract_generator", label: "Contrato (PDF)", desc: "Gera contratos novos com IA a partir de um modelo de referência" },
  { skill_key: "appointment_scheduler", label: "Agendamento", desc: "Agenda consultas, reuniões e entrevistas" },
  { skill_key: "sdr", label: "SDR comercial", desc: "Qualifica leads e agenda reuniões com vendedores" },
];

const SkillSelector = ({
  value = [],
  onChange,
  disabled = false,
  showTicketSellerSkill = true,
  includeRoadPassengerCopy = true,
}) => {
  const { t } = useTranslation("settings");
  const configBySkillRef = useRef({});
  const current = Array.isArray(value) ? value : [];
  const primary = current[0];
  const selectedKey =
    typeof primary === "object" && primary?.skill_key
      ? primary.skill_key
      : typeof primary === "string"
        ? primary
        : null;

  useEffect(() => {
    const row = Array.isArray(value) ? value[0] : null;
    if (row && typeof row === "object" && row.skill_key) {
      const c = row.config && typeof row.config === "object" && !Array.isArray(row.config) ? row.config : {};
      configBySkillRef.current[row.skill_key] = { ...c };
    }
  }, [value]);

  const visibleSkillKeys = useMemo(
    () => {
      let keys = SKILL_OPTION_KEYS;
      if (!showTicketSellerSkill) {
        keys = keys.filter(
          (k) => k !== "ticket_seller" && k !== "ticket_preseller" && k !== "passagem_recovery"
        );
      }
      return keys;
    },
    [showTicketSellerSkill]
  );

  const defs = visibleSkillKeys.map((skill_key) => ({
    skill_key,
    label:
      skill_key === "receptionist"
        ? t("agentsListPage.skillReceptionist")
        : skill_key === "faq"
          ? t("agentsListPage.skillFaq")
          : skill_key === "proposal_generator"
            ? t("agentsListPage.skillProposalGenerator")
            : skill_key === "contract_generator"
              ? t("agentsListPage.skillContractGenerator")
            : skill_key === "appointment_scheduler"
              ? t("agentsListPage.skillAppointmentScheduler")
            : skill_key === "sdr"
              ? t("agentsListPage.skillSdr")
              : skill_key === "ticket_seller"
                ? t("agentsListPage.skillTicketSeller")
              : skill_key === "ticket_preseller"
                ? t("agentsListPage.skillTicketPreseller")
              : skill_key === "passagem_recovery"
                ? t("agentsListPage.skillPassagemRecovery")
              : skill_key === "social_media"
                ? t("agentsListPage.skillSocialMedia")
              : skill_key === "product_seller"
                ? t("agentsListPage.skillProductSeller")
              : skill_key === "complaints_suggestions"
                ? t("agentsListPage.skillComplaintsSuggestions")
              : skill_key === "ops_intelligence"
                ? t("agentsListPage.skillOpsIntelligence")
              : skill_key,
    desc:
      skill_key === "receptionist"
        ? t(
            includeRoadPassengerCopy
              ? "agentsListPage.skillReceptionistDesc"
              : "agentsListPage.skillReceptionistDescGeneric"
          )
        : skill_key === "faq"
          ? t("agentsListPage.skillFaqDesc")
          : skill_key === "proposal_generator"
            ? t("agentsListPage.skillProposalGeneratorDesc")
            : skill_key === "contract_generator"
              ? t("agentsListPage.skillContractGeneratorDesc")
              : skill_key === "appointment_scheduler"
                ? t("agentsListPage.skillAppointmentSchedulerDesc")
                : skill_key === "sdr"
                  ? t("agentsListPage.skillSdrDesc")
                  : skill_key === "ticket_seller"
                    ? t("agentsListPage.skillTicketSellerDesc")
                  : skill_key === "ticket_preseller"
                    ? t("agentsListPage.skillTicketPresellerDesc")
                  : skill_key === "passagem_recovery"
                    ? t("agentsListPage.skillPassagemRecoveryDesc")
                  : skill_key === "social_media"
                    ? t("agentsListPage.skillSocialMediaDesc")
                  : skill_key === "product_seller"
                    ? t("agentsListPage.skillProductSellerDesc")
                  : skill_key === "complaints_suggestions"
                    ? t("agentsListPage.skillComplaintsSuggestionsDesc")
                  : skill_key === "ops_intelligence"
                    ? t("agentsListPage.skillOpsIntelligenceDesc")
                  : "",
  }));

  const selectSkill = (skill_key) => {
    if (disabled) return;
    if (selectedKey === skill_key) return;
    const order_index = Math.max(0, SKILL_OPTION_KEYS.indexOf(skill_key));
    const cached = configBySkillRef.current[skill_key];
    const config = cached && typeof cached === "object" && !Array.isArray(cached) ? { ...cached } : {};
    onChange?.([{ skill_key, config, order_index }]);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.45 }}>
        {t("agentsListPage.skillSelectorSingleHint")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 1.5,
        }}
      >
        {defs.map(({ skill_key, label, desc }) => {
          const selected = selectedKey === skill_key;
          return (
            <Paper
              key={skill_key}
              component="button"
              type="button"
              variant="outlined"
              onClick={() => selectSkill(skill_key)}
              disabled={disabled}
              sx={{
                p: 1.75,
                minHeight: 96,
                textAlign: "left",
                cursor: disabled ? "default" : "pointer",
                borderWidth: 1.5,
                borderColor: selected ? "primary.main" : "divider",
                bgcolor: (theme) =>
                  selected ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
                opacity: disabled ? 0.65 : 1,
                borderRadius: 2,
                boxShadow: selected ? 2 : 0,
                transition: "border-color 0.15s, box-shadow 0.15s, background-color 0.15s",
                "&:hover": disabled
                  ? {}
                  : {
                      borderColor: "primary.main",
                      boxShadow: 1,
                    },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, height: "100%" }}>
                {selected ? (
                  <RadioButtonCheckedRoundedIcon color="primary" sx={{ fontSize: 22, mt: 0.125, flexShrink: 0 }} />
                ) : (
                  <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 22, mt: 0.125, flexShrink: 0, color: "action.active" }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.35, mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.45 }}>
                    {desc}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default SkillSelector;
